import Backbone from 'backbone';
import React from 'react';

import {AnnotationAttributes, AnnotationCollection} from 'biojs/Annotation';
import {NucleotideType} from 'biojs/BioConstants';
import {PartAttributes, Parts} from 'biojs/Part';
import {ModifiedNucleotides, TranslationGroup} from 'biojs/Sequence';
import {SequencePrimerAttributes, SequencePrimerCollection} from 'biojs/SequencePrimer';
import {TranslationAttributes, TranslationCollection} from 'biojs/Translation';
import localStorage from 'LocalStorage';
import {NonEmptySelection} from 'molbio/maps/selectables/Selection';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';

const MAX_CLIPBOARD_LENGTH = 300000; // FIXME: find max value before clipboard not set.

const hashRegex = /^\[Benchling \| .+\]$/;

export interface ClipboardMeta {
  'copied-annotations'?: AnnotationCollection;
  'copied-translations'?: TranslationCollection;
  'copied-translation-groups'?: Array<TranslationGroup>;
  'copied-sequencePrimers'?: SequencePrimerCollection;
  'copied-parts'?: Parts;
  'copied-enzymes'?: [number, number];
  'copied-modified-nucleotides'?: ModifiedNucleotides;
  'copied-sourceNucleotideType'?: NucleotideType;
}

// Note: These types are somewhat inaccurate - a lot of the attributes get dropped in serialization
export interface SerializedClipboardMeta {
  'copied-annotations'?: Array<AnnotationAttributes>;
  'copied-translations'?: Array<TranslationAttributes>;
  'copied-translation-groups'?: Array<TranslationGroup>;
  'copied-sequencePrimers'?: Array<SequencePrimerAttributes>;
  'copied-parts'?: Array<PartAttributes>;
  'copied-enzymes'?: [number, number];
  'copied-modified-nucleotides'?: ModifiedNucleotides;
  'copied-sourceNucleotideType'?: NucleotideType;
}

export enum ClipboardGrabState {
  CLIPBOARD = 'clipboard',
  STALE = 'stale',
  RAW = 'raw',
}

interface BenchlingClipboardAttributes {
  hash: null | string;
  text: null | string;
  meta: null | string;
  maxLength: number;
}
interface BenchlingClipboard {
  get<K extends keyof BenchlingClipboardAttributes>(key: K): BenchlingClipboardAttributes[K];
  set(obj: Partial<BenchlingClipboardAttributes>): BenchlingClipboard;
  set<K extends keyof BenchlingClipboardAttributes>(
    key: K,
    value: BenchlingClipboardAttributes[K]
  ): BenchlingClipboard;
}
class BenchlingClipboard extends Backbone.Model {
  defaults(): {hash: null; text: null; meta: null; maxLength: number} {
    return {
      hash: null,
      text: null,
      meta: null,
      maxLength: MAX_CLIPBOARD_LENGTH,
    };
  }

  isHash(hash: string): boolean {
    return hashRegex.test(hash);
  }

  generateHash(desc: string): string {
    return `[Benchling | ${desc}]`;
  }

  getHash(text: string, desc: string = this.defaultDesc()): string {
    if (text.length > this.get('maxLength')) {
      return this.generateHash(desc);
    } else {
      return text;
    }
  }

  defaultDesc(): string {
    return new Date().toString();
  }

  stdDesc(selection: NonEmptySelection): string {
    const sequence = selection.get('sequence');
    const name = sequence.name();
    const start = sequtil.modelStartToDisplay(selection.start(), sequence.startIndex());
    const end = sequtil.modelEndToDisplay(selection.end(), sequence.length(), sequence.startIndex());
    return `${name}: ${start}-${end}bp`;
  }

  /**
   * @param {string} hashDesc - description for the copied hash
   * @param {string} copyDescription - description for what is being copied (will be shown on the toast)
   */
  store(
    text: string,
    meta: ClipboardMeta = {},
    hashDesc: string = this.defaultDesc(),
    copyDescription: string = 'Copied to clipboard:'
  ): {hash: string; hashed: boolean; onlyThisWindow: boolean} {
    // Returns a hash that should be put in the input used for forcing a save.
    let storedInLS = false;
    let onlyThisWindow = false; // if only this window will have access

    // if a hash is going to be stored on the clipboard.
    const hashed = text.length > this.get('maxLength');
    const hash = this.getHash(text, hashDesc);

    // First, try to save in localStorage
    if (localStorage.enabled()) {
      try {
        localStorage.setItem('clipboard-text', text, {reraise: true});
        localStorage.setItem('clipboard-meta', JSON.stringify(meta), {reraise: true});
        localStorage.setItem('clipboard-hash', hash, {reraise: true});
        storedInLS = true;
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        // So there is no confusion.
        localStorage.removeItem('clipboard-text');
        localStorage.removeItem('clipboard-meta');
        localStorage.removeItem('clipboard-hash');
        storedInLS = false;
      }
    }

    // Otherwise, store it on BenchlingClipboard
    if (!storedInLS) {
      // FIXME: Will stringify be slow for large copy? This handles converting collections
      // to arrays, which is what the caller is expecting. There would be some weird logic
      // to convert things to arrays, or have the caller expect collection or array...
      // In the ideal world, we just store meta.

      this.set('meta', JSON.stringify(meta));
      this.set('text', text);
      this.set('hash', hash);
      onlyThisWindow = true;
    } else {
      this.set({text: null, meta: null, hash: null});
    }

    const copied = text.length > 35 ? `${text.substring(0, 35)}...` : text;

    notifications.success(
      <>
        {copyDescription} <code>{copied}</code>
      </>,
      {id: 'copy-to-clipboard'}
    );

    return {
      hash,
      hashed,
      onlyThisWindow,
    };
  }

  grab(hash: string): {text: string; meta: SerializedClipboardMeta; state: ClipboardGrabState} {
    // Returns a dictionary with attrs 'text' and 'meta'

    // First we check local storage.
    if (localStorage.enabled()) {
      const lsHash = localStorage.getItem('clipboard-hash');
      if (hash === lsHash) {
        const lsMeta = JSON.parse(localStorage.getItem('clipboard-meta') as string);
        const lsText = localStorage.getItem('clipboard-text') as string;
        return {text: lsText, meta: lsMeta, state: ClipboardGrabState.CLIPBOARD};
      }
    }

    // Then we try BenchlingClipboard
    const bcHash = this.get('hash');
    if (hash === bcHash) {
      const bcMeta = JSON.parse(this.get('meta')!);
      const bcText = this.get('text')!;
      return {text: bcText, meta: bcMeta, state: ClipboardGrabState.CLIPBOARD};
    }

    this.clear(); // nothing we found we liked, so kill everything!

    if (this.isHash(hash)) {
      // probably good signal to prevent the actual paste.
      notifications.error('The clipboard data was lost. Retry the copy and paste.');
      return {text: '', meta: {}, state: ClipboardGrabState.STALE};
    }

    return {text: hash, meta: {}, state: ClipboardGrabState.RAW}; // nothing stored, let it through.
  }

  clear(): void {
    if (localStorage.enabled()) {
      localStorage.removeItem('clipboard-meta');
      localStorage.removeItem('clipboard-text');
      localStorage.removeItem('clipboard-hash');
    }
    this.set({text: null, meta: null, hash: null});
  }
}

export const clipboard = new BenchlingClipboard(); // singleton

export {
  // for tests
  BenchlingClipboard as Model,
};
